import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Building2,
  Castle,
  Circle,
  Crown,
  Leaf,
  Minus,
  Mountain,
  Radio,
  Sofa,
  Sparkles,
  Sun,
  TreePine,
  Waves,
  Wheat,
  Wind,
  Wrench,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import {
  desiredStyleSettingsAtom,
  roomModeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import { ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES } from '../../constants/room-mode';
import {
  DESIRED_STYLE_OPTIONS,
  desiredStyleOptions,
  type TYPE_DESIRED_STYLE_OPTIONS,
} from '../../constants/room-style';
import { SettingsSectionHeader } from './settings-section-header';

const styleIcons = {
  [DESIRED_STYLE_OPTIONS.ELEGANT]: Crown,
  [DESIRED_STYLE_OPTIONS.SUSTAINABLE_DESIGN]: Leaf,
  [DESIRED_STYLE_OPTIONS.CURVES_AND_ORGANIC_SHAPES]: Circle,
  [DESIRED_STYLE_OPTIONS.RETRO_AND_VINTAGE]: Radio,
  [DESIRED_STYLE_OPTIONS.MINIMALIST]: Minus,
  [DESIRED_STYLE_OPTIONS.INDUSTRIAL]: Wrench,
  [DESIRED_STYLE_OPTIONS.SCANDINAVIAN]: TreePine,
  [DESIRED_STYLE_OPTIONS.RUSTIC]: Mountain,
  [DESIRED_STYLE_OPTIONS.CONTEMPORARY]: Building2,
  [DESIRED_STYLE_OPTIONS.MID_CENTURY_MODERN]: Sofa,
  [DESIRED_STYLE_OPTIONS.FRENCH_COUNTRY]: Wheat,
  [DESIRED_STYLE_OPTIONS.COASTAL]: Waves,
  [DESIRED_STYLE_OPTIONS.ART_DECO]: Sparkles,
  [DESIRED_STYLE_OPTIONS.BOHO_CHIC]: Wind,
  [DESIRED_STYLE_OPTIONS.URBAN_MODERN]: Building2,
  [DESIRED_STYLE_OPTIONS.MEDITERRANEAN]: Sun,
  [DESIRED_STYLE_OPTIONS.MOROCCAN]: Sparkles,
  [DESIRED_STYLE_OPTIONS.VICTORIAN]: Castle,
};

const styleAccentColors = {
  [DESIRED_STYLE_OPTIONS.ELEGANT]: '#8b7656',
  [DESIRED_STYLE_OPTIONS.SUSTAINABLE_DESIGN]: '#16a34a',
  [DESIRED_STYLE_OPTIONS.CURVES_AND_ORGANIC_SHAPES]: '#e11d48',
  [DESIRED_STYLE_OPTIONS.RETRO_AND_VINTAGE]: '#ea580c',
  [DESIRED_STYLE_OPTIONS.MINIMALIST]: '#475569',
  [DESIRED_STYLE_OPTIONS.INDUSTRIAL]: '#4b5563',
  [DESIRED_STYLE_OPTIONS.SCANDINAVIAN]: '#0ea5e9',
  [DESIRED_STYLE_OPTIONS.RUSTIC]: '#b45309',
  [DESIRED_STYLE_OPTIONS.CONTEMPORARY]: '#2563eb',
  [DESIRED_STYLE_OPTIONS.MID_CENTURY_MODERN]: '#0891b2',
  [DESIRED_STYLE_OPTIONS.FRENCH_COUNTRY]: '#7dd3fc',
  [DESIRED_STYLE_OPTIONS.COASTAL]: '#06b6d4',
  [DESIRED_STYLE_OPTIONS.ART_DECO]: '#f59e0b',
  [DESIRED_STYLE_OPTIONS.BOHO_CHIC]: '#f97316',
  [DESIRED_STYLE_OPTIONS.URBAN_MODERN]: '#334155',
  [DESIRED_STYLE_OPTIONS.MEDITERRANEAN]: '#eab308',
  [DESIRED_STYLE_OPTIONS.MOROCCAN]: '#dc2626',
  [DESIRED_STYLE_OPTIONS.VICTORIAN]: '#7c3aed',
};

export const DesiredStyleSettings = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const mode = useAtomValue(roomModeSettingsAtom);
  const [desiredStyle, setDesiredStyle] = useAtom(desiredStyleSettingsAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);
  const isDisabled = ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES.includes(mode);

  useEffect(() => {
    setIsOpen(!isDisabled);
  }, [isDisabled]);

  const handleSelect = (style: TYPE_DESIRED_STYLE_OPTIONS) => {
    if (isDisabled) return;

    setDesiredStyle(style);
    setSettingsChanged(true);
  };

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-sm border border-slate-200 bg-white sm:w-[287px]',
        isDisabled && 'bg-slate-50 text-slate-400'
      )}
    >
      <SettingsSectionHeader
        label={t('style')}
        value={!isDisabled && desiredStyle ? t(desiredStyle) : undefined}
        isOpen={isOpen}
        disabled={isDisabled}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen && (
        <div className="grid grid-cols-3 gap-2 px-3 pb-3">
          {desiredStyleOptions.map((option) => {
            const Icon = styleIcons[option.value];
            const isSelected = option.value === desiredStyle;
            const accentColor = styleAccentColors[option.value];

            return (
              <button
                key={option.value}
                type="button"
                disabled={isDisabled}
                aria-label={t(option.value)}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'flex h-[59px] w-full min-w-0 flex-col items-center justify-center gap-1 rounded-sm border px-1.5 text-center text-[9px] font-semibold leading-[11px] transition-colors',
                  isSelected
                    ? 'border-slate-200 bg-primary-light/10 text-primary-light'
                    : 'border-slate-200 bg-[#f0f2f580] text-slate-500 hover:border-primary-light',
                  isDisabled &&
                    'cursor-not-allowed bg-slate-100 text-slate-400 hover:border-slate-200'
                )}
                style={
                  isSelected
                    ? {
                        borderLeftColor: accentColor,
                        borderLeftWidth: 4,
                      }
                    : undefined
                }
              >
                <Icon
                  aria-hidden
                  className="size-4 shrink-0"
                  style={isSelected ? { color: accentColor } : undefined}
                />
                <span className="w-full whitespace-normal [overflow-wrap:normal] [word-break:normal]">
                  {t(option.value)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
